import React, { useState, useEffect, useMemo, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Eye, Trash2, Printer, Filter, X, Plus, Search, Mail, AlertTriangle, Info } from "lucide-react";
import { motion } from "framer-motion";
import { formatCurrency } from "@/components/inventory/InventoryUtils";
import { PendingOrders } from "./PendingOrders";
import { supabase } from "@/lib/supabase";
// Importación directa para React PDF
import { PDFDownloadLink } from '@react-pdf/renderer';
import { OrderPDF } from './OrderPDF';
import { CancellationDialog } from './CancellationDialog';
import { CancelledOrdersList } from './CancelledOrdersList';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShippingAddressSelector } from "@/components/ShippingAddressSelector";
import { ProductSelector } from "@/components/ProductSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { fetchSeasonDatePresets, toDateRangeFromPreset } from "@/lib/seasonDatePresets";

// Constantes para almacenamiento
const STORAGE_KEY = 'orderList_filters';
const SEARCH_STORAGE_KEY = 'orderList_search';
const MAX_STORAGE_SIZE = 1024 * 1024; // 1MB límite
const ORDER_STATUS_OPTIONS = [
  { value: "pending", label: "Pendiente" },
  { value: "partially_served", label: "Servido parcialmente" },
  { value: "partially_served_cancelled", label: "Parcialmente servido cancelado" },
  { value: "served", label: "Servido" },
];

// Función para detectar si localStorage está disponible
const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

// Función para obtener el storage apropiado
const getStorage = () => {
  if (isLocalStorageAvailable()) {
    return localStorage;
  }
  // Fallback a sessionStorage si localStorage no está disponible
  return sessionStorage;
};

// Función para guardar datos de forma segura
const safeSetItem = (key, value) => {
  try {
    const storage = getStorage();
    const serialized = JSON.stringify(value);
    
    // Verificar tamaño antes de guardar
    if (serialized.length > MAX_STORAGE_SIZE) {
      console.warn(`Datos demasiado grandes para guardar en storage: ${key}`);
      return false;
    }
    
    storage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error('Error saving to storage:', error);
    return false;
  }
};

// Función para leer datos de forma segura
const safeGetItem = (key, defaultValue = null) => {
  try {
    const storage = getStorage();
    const item = storage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error reading from storage:', error);
    return defaultValue;
  }
};

const normalizeStatusFilter = (statusValue) => {
  const validStatuses = new Set(ORDER_STATUS_OPTIONS.map((option) => option.value));

  if (Array.isArray(statusValue)) {
    return statusValue.filter((status) => validStatuses.has(status));
  }

  if (typeof statusValue === "string" && validStatuses.has(statusValue)) {
    return [statusValue];
  }

  return [];
};

// Función para guardar filtros en storage
const saveFiltersToStorage = (filters) => {
  const filtersToSave = {
    ...filters,
    dateRange: {
      from: filters.dateRange.from ? filters.dateRange.from.toISOString() : null,
      to: filters.dateRange.to ? filters.dateRange.to.toISOString() : null,
    },
    deliveryDateRange: {
      from: filters.deliveryDateRange?.from ? filters.deliveryDateRange.from.toISOString() : null,
      to: filters.deliveryDateRange?.to ? filters.deliveryDateRange.to.toISOString() : null,
    },
    dateRangePresetId: filters.dateRangePresetId || "__none__",
    deliveryDateRangePresetId: filters.deliveryDateRangePresetId || "__none__",
    timestamp: Date.now() // Agregar timestamp para limpieza automática
  };
  
  return safeSetItem(STORAGE_KEY, filtersToSave);
};

// Función para cargar filtros desde storage
const loadFiltersFromStorage = () => {
  const saved = safeGetItem(STORAGE_KEY);
  
  if (saved && saved.timestamp) {
    // Limpiar filtros antiguos (más de 24 horas)
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    if (saved.timestamp < oneDayAgo) {
      clearStorage();
      return getDefaultFilters();
    }
    
    return {
      ...saved,
      status: normalizeStatusFilter(saved.status),
      dateRange: {
        from: saved.dateRange?.from ? new Date(saved.dateRange.from) : null,
        to: saved.dateRange?.to ? new Date(saved.dateRange.to) : null,
      },
      deliveryDateRange: {
        from: saved.deliveryDateRange?.from ? new Date(saved.deliveryDateRange.from) : null,
        to: saved.deliveryDateRange?.to ? new Date(saved.deliveryDateRange.to) : null,
      },
      dateRangePresetId: saved.dateRangePresetId || "__none__",
      deliveryDateRangePresetId: saved.deliveryDateRangePresetId || "__none__",
    };
  }
  
  return getDefaultFilters();
};

// Función para obtener filtros por defecto
const getDefaultFilters = () => ({
  status: [],
  dateRange: {
    from: null,
    to: null,
  },
  dateRangePresetId: "__none__",
  deliveryDateRange: {
    from: null,
    to: null,
  },
  deliveryDateRangePresetId: "__none__",
  paymentMethod: "all",
  representative: "all",
  productId: null
});

// Función para guardar búsqueda en storage
const saveSearchToStorage = (searchTerm) => {
  return safeSetItem(SEARCH_STORAGE_KEY, {
    term: searchTerm,
    timestamp: Date.now()
  });
};

// Función para cargar búsqueda desde storage
const loadSearchFromStorage = () => {
  const saved = safeGetItem(SEARCH_STORAGE_KEY);
  
  if (saved && saved.timestamp) {
    // Limpiar búsquedas antiguas (más de 1 hora)
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    if (saved.timestamp < oneHourAgo) {
      clearStorage();
      return "";
    }
    
    return saved.term || "";
  }
  
  return "";
};

// Función para limpiar storage
const clearStorage = () => {
  try {
    const storage = getStorage();
    storage.removeItem(STORAGE_KEY);
    storage.removeItem(SEARCH_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
};

export function OrderList() {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState(loadSearchFromStorage);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(loadSearchFromStorage);
  const [viewMode, setViewMode] = useState("list");
  const [loading, setLoading] = useState(true);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [showCancellationDialog, setShowCancellationDialog] = useState(false);
  const [showCancelledOrders, setShowCancelledOrders] = useState(false);
  const [showPendingInfoDialog, setShowPendingInfoDialog] = useState(false);
  const [selectedPendingInfoOrder, setSelectedPendingInfoOrder] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const { toast } = useToast();
  const [pdfOrder, setPdfOrder] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [shippingAddresses, setShippingAddresses] = useState({});
  const [emailOrder, setEmailOrder] = useState(null);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userCodRep, setUserCodRep] = useState(null);
  const [canViewAllOrders, setCanViewAllOrders] = useState(false);
  const [representatives, setRepresentatives] = useState([]);
  const [filters, setFilters] = useState(loadFiltersFromStorage);
  const [showFilters, setShowFilters] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [filtersRestored, setFiltersRestored] = useState(false);
  const [seasonDatePresets, setSeasonDatePresets] = useState([]);
  // Reducir tamaño de página en tablets/móviles para mejorar rendimiento
  const isSmallDevice = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 1024px)').matches;
  const ITEMS_PER_PAGE = isSmallDevice ? 15 : 40;
  const reduceMotion = isSmallDevice;

  // Función auxiliar para aplicar filtros a una consulta
  const applyFiltersToQuery = (query) => {
    if (userData?.role !== 'admin' && !userData?.ver_comisiones_globales) {
      query = query.eq('cod_rep', userData.cod_rep);
    } else if (isFiltering && filters.representative !== 'all') {
      query = query.eq('cod_rep', filters.representative);
    }

    if (filters.status?.length > 0) {
      query = query.in('status', filters.status);
    }

    if (filters.dateRange.from) {
      query = query.gte('date', filters.dateRange.from.toISOString());
    }

    if (filters.dateRange.to) {
      query = query.lte('date', filters.dateRange.to.toISOString());
    }

    if (filters.deliveryDateRange?.from) {
      query = query.gte('fecha_entrega', format(filters.deliveryDateRange.from, "yyyy-MM-dd"));
    }

    if (filters.deliveryDateRange?.to) {
      query = query.lte('fecha_entrega', format(filters.deliveryDateRange.to, "yyyy-MM-dd"));
    }

    if (filters.paymentMethod !== 'all') {
      // Filtrar por coincidencia exacta del array de forma de pago
      const paymentValue = parseInt(filters.paymentMethod);
      if (paymentValue === 0) {
        // Para contado, buscar arrays vacíos []
        query = query.eq('forma_pago', '{}');
      } else if (paymentValue === 30) {
        // Para 30 días, buscar arrays que solo contengan [30]
        query = query.eq('forma_pago', '{30}');
      } else if (paymentValue === 60) {
        // Para 60 días, buscar arrays que contengan exactamente [30, 60]
        query = query.eq('forma_pago', '{30,60}');
      } else if (paymentValue === 90) {
        // Para 90 días, buscar arrays que contengan exactamente [30, 60, 90]
        query = query.eq('forma_pago', '{30,60,90}');
      }
    }

    return query;
  };

  const fetchUserData = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      if (!user) throw new Error('No hay usuario autenticado');

      const { data, error: roleError } = await supabase
        .from('users')
        .select('role, cod_rep, ver_comisiones_globales')
        .eq('id', user.id)
        .single();

      if (roleError) throw roleError;
      
      setUserRole(data?.role);
      setUserCodRep(data?.cod_rep);
      setCanViewAllOrders(data?.ver_comisiones_globales || data?.role === 'admin');
      setUserData(data);
      
      return data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos del usuario",
        variant: "destructive",
      });
      return null;
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('nombre_empresa, calle_empresa, poblacion_empresa, provincia_empresa, cif_empresa, color_mappings')
        .single();

      if (error) throw error;
      setCompanyInfo(data);
    } catch (error) {
      console.error('Error fetching company info:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos de la empresa",
        variant: "destructive",
      });
    }
  };

  const fetchRepresentatives = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('name, cod_rep')
        .eq('role', 'vendedor')
        .order('name');

      if (error) throw error;
      setRepresentatives(data || []);
    } catch (error) {
      console.error('Error fetching representatives:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los representantes",
        variant: "destructive",
      });
    }
  };

  const loadSeasonDatePresets = async () => {
    const presets = await fetchSeasonDatePresets();
    setSeasonDatePresets((presets || []).filter((p) => p.active !== false));
  };

  const getMatchingPresetId = (range) => {
    if (!range?.from || !range?.to || seasonDatePresets.length === 0) return "__none__";

    const from = format(range.from, "yyyy-MM-dd");
    const to = format(range.to, "yyyy-MM-dd");
    const matchingPreset = seasonDatePresets.find((preset) => preset.from === from && preset.to === to);

    return matchingPreset?.id || "__none__";
  };

  const fetchOrders = async () => {
    if (!userData) return;

    try {
      setLoading(true);
      
      // Preconsulta: si hay filtro por producto, obtener IDs de pedidos que lo contienen
      let filteredOrderIds = null;
      if (filters?.productId) {
        const { data: orderItems, error: itemsError } = await supabase
          .from('order_items')
          .select('order_id')
          .eq('product_id', filters.productId);

        if (itemsError) throw itemsError;

        const ids = [...new Set((orderItems || []).map(oi => oi.order_id))];
        if (ids.length === 0) {
          setOrders([]);
          setTotalOrders(0);
          setTotalPages(1);
          setLoading(false);
          return;
        }
        filteredOrderIds = ids;
      }
      
      // Selección mínima para la lista
      let query = supabase
        .from('orders')
        .select(`
          id,
          number,
          date,
          total,
          status,
          notes,
          fecha_entrega,
          forma_pago,
          cod_rep,
          signature,
          confirmacion_90dias,
          shipping_address_id,
          discount_percentage,
          payment_transfer,
          customer:customers(
            name,
            email,
            sepa_signature
          )
        `, { count: 'exact' })
        .order('number', { ascending: false })
        .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

      // Aplicar filtros primero
      query = applyFiltersToQuery(query);

      // Aplicar filtro por producto si corresponde
      if (filteredOrderIds) {
        query = query.in('id', filteredOrderIds);
      }

      // Aplicar búsqueda
      if (appliedSearchTerm.trim()) {
        const searchValue = appliedSearchTerm.trim();
        const isNumeric = !isNaN(searchValue) && searchValue !== '';
        
        if (isNumeric) {
          // Si es numérico, buscar por número de pedido (exacto)
          query = query.eq('number', parseInt(searchValue));
        } else {
          // Si no es numérico, buscar por nombre del cliente
          // Primero obtener los IDs de clientes que coincidan
          const { data: customerIds, error: customerError } = await supabase
            .from('customers')
            .select('id')
            .ilike('name', `%${searchValue}%`);

          if (customerError) throw customerError;

          if (customerIds && customerIds.length > 0) {
            const ids = customerIds.map(c => c.id);
            query = query.in('customer_id', ids);
          } else {
            // Si no hay clientes que coincidan, devolver resultados vacíos
            setOrders([]);
            setTotalOrders(0);
            setTotalPages(1);
            setLoading(false);
            return;
          }
        }
      }

      const { data, error, count } = await query;

      if (error) throw error;

      setOrders(data || []);
      setTotalOrders(count || 0);
      setTotalPages(Math.ceil((count || 0) / ITEMS_PER_PAGE));
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los pedidos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar conectividad
  const checkConnectivity = async () => {
    try {
      // Intentar hacer una consulta simple a Supabase
      const { data, error } = await supabase
        .from('orders')
        .select('id')
        .limit(1);
      
      if (error) {
        console.error('Connectivity check failed:', error);
        return false;
      }
      
      console.log('Connectivity check passed');
      return true;
    } catch (error) {
      console.error('Connectivity check error:', error);
      return false;
    }
  };

        // Función para cargar los detalles de un pedido específico con retry
      const fetchOrderDetails = async (orderId, retryCount = 0) => {
        const maxRetries = 3;
        
        try {
          console.log(`Fetching order details for order ID: ${orderId} (attempt ${retryCount + 1})`);
          
          // Verificar conectividad antes de hacer la consulta
          if (retryCount === 0) {
            const isConnected = await checkConnectivity();
            if (!isConnected) {
              throw new Error('No hay conexión con la base de datos. Verifique su conexión a internet.');
            }
          }
          
          const { data, error } = await supabase
            .from('orders')
            .select(`
              id,
              number,
              date,
              total,
              status,
              notes,
              fecha_entrega,
              forma_pago,
              cod_rep,
              signature,
              confirmacion_90dias,
              shipping_address_id,
              discount_percentage,
              payment_transfer,
          customer:customers(
            name,
            email,
            phone,
            titular,
            street,
            city,
            province,
            postalCode,
            sepa_signature
          ),
          items:order_items(
            id,
            quantity,
            pending_quantity,
            price,
            size,
            color,
            product:products(
              id,
              code,
              description,
              images,
              main_image_index
            )
          ),
          shipping_address:shipping_addresses(
            id,
            name,
            street,
            city
          )
        `)
        .eq('id', orderId)
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Order details fetched successfully:', data);

      // Validar que los datos sean válidos
      if (!data) {
        throw new Error('No se encontró el pedido en la base de datos');
      }

      if (!data.items || data.items.length === 0) {
        console.warn('Order has no items:', data);
        throw new Error('El pedido no tiene líneas de productos asociadas');
      }

      // Verificar que cada item tenga los datos necesarios
      const invalidItems = data.items.filter(item => 
        !item.product || !item.product.id
      );
      
      if (invalidItems.length > 0) {
        console.warn('Some items have missing product data:', invalidItems);
      }

      // Actualizar el pedido con los detalles usando una función de actualización más robusta
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order.id === orderId) {
            console.log('Updating order in state with items:', data.items);
            const updatedOrder = {
              ...order,
              items: data.items,
              shipping_address: data.shipping_address,
              discount_percentage: data.discount_percentage,
              payment_transfer: data.payment_transfer
            };
            console.log('Updated order object:', updatedOrder);
            return updatedOrder;
          }
          return order;
        });
        
        console.log('Updated orders state:', updatedOrders);
        return updatedOrders;
      });

      // Actualizar el mapa de direcciones
      if (data.shipping_address) {
        setShippingAddresses(prev => ({
          ...prev,
          [orderId]: data.shipping_address
        }));
      }

      console.log('Order details updated in state successfully');
      return data;
    } catch (error) {
      console.error(`Error fetching order details (attempt ${retryCount + 1}):`, error);
      
      // Proporcionar mensajes de error más específicos
      let errorMessage = 'Error desconocido al cargar los detalles del pedido';
      
      if (error.code === 'PGRST116') {
        errorMessage = 'El pedido no existe en la base de datos';
      } else if (error.code === 'PGRST301') {
        errorMessage = 'Error de conexión con la base de datos';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.includes('network') || error.includes('fetch')) {
        errorMessage = 'Error de conexión a internet. Verifique su conexión e inténtelo de nuevo.';
      }
      
      if (retryCount < maxRetries) {
        // Esperar antes de reintentar (backoff exponencial)
        const delay = Math.pow(2, retryCount) * 1000;
        console.log(`Retrying in ${delay}ms...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchOrderDetails(orderId, retryCount + 1);
      }
      
      // Crear un error con el mensaje específico
      const specificError = new Error(errorMessage);
      specificError.originalError = error;
      throw specificError;
    }
  };

  // Función para cargar detalles de direcciones de envío
  const fetchShippingAddresses = async () => {
    try {
      // Obtener todos los pedidos que tienen shipping_address_id
      const ordersWithShipping = orders.filter(order => order.shipping_address_id);
      
      if (ordersWithShipping.length === 0) return;

      // Obtener todos los IDs de direcciones de envío únicos
      const shippingIds = [...new Set(ordersWithShipping.map(order => order.shipping_address_id))];
      
      const { data, error } = await supabase
        .from('shipping_addresses')
        .select('id, name, street, city')
        .in('id', shippingIds);

      if (error) throw error;

      // Crear un mapa de direcciones
      const addressMap = {};
      data.forEach(address => {
        addressMap[address.id] = address;
      });

      // Actualizar el estado de direcciones de envío
      setShippingAddresses(prev => {
        const newAddresses = { ...prev };
        ordersWithShipping.forEach(order => {
          if (addressMap[order.shipping_address_id]) {
            newAddresses[order.id] = addressMap[order.shipping_address_id];
          }
        });
        return newAddresses;
      });
    } catch (error) {
      console.error('Error fetching shipping addresses:', error);
    }
  };

  // Inicialización
  useEffect(() => {
    const initialize = async () => {
      const user = await fetchUserData();
      if (user) {
        // Ejecutar fetchOrders primero para mostrar los datos principales
        await fetchOrders();
        // Ejecutar las otras funciones en paralelo ya que no son críticas para la vista inicial
        Promise.all([
          fetchCompanyInfo(),
          fetchRepresentatives(),
          loadSeasonDatePresets()
        ]).catch(error => {
          console.error('Error loading additional data:', error);
        });
      }
    };
    initialize();
  }, []);

  // Inicializar estado de filtrado basado en filtros cargados
  useEffect(() => {
    const wasFiltering = isFiltering;
    setIsFiltering(filters.representative !== 'all');
    
    // Mostrar toast si los filtros se restauraron y hay filtros activos
    if (!wasFiltering && filters.representative !== 'all') {
      toast({
        title: "Filtros restaurados",
        description: "Se han restaurado los filtros de la sesión anterior",
        duration: 3000,
      });
    }
    
    // Marcar que los filtros han sido restaurados
    setFiltersRestored(true);
  }, [filters.representative, isFiltering, toast]);

  // Recargar pedidos cuando cambien los filtros, la página o la búsqueda aplicada
  useEffect(() => {
    if (userData) {
      fetchOrders();
    }
  }, [filters, userData, currentPage, appliedSearchTerm]);

  // Cargar direcciones de envío cuando se carguen los pedidos
  useEffect(() => {
    if (orders.length > 0) {
      fetchShippingAddresses();
    }
  }, [orders]);

  // Efecto separado para resetear página cuando cambien los filtros
  useEffect(() => {
    if (userData) {
      setCurrentPage(1);
    }
  }, [filters, userData]);

  useEffect(() => {
    if (seasonDatePresets.length === 0) return;

    const nextDateRangePresetId = getMatchingPresetId(filters.dateRange);
    const nextDeliveryDateRangePresetId = getMatchingPresetId(filters.deliveryDateRange);

    if (
      nextDateRangePresetId === (filters.dateRangePresetId || "__none__") &&
      nextDeliveryDateRangePresetId === (filters.deliveryDateRangePresetId || "__none__")
    ) {
      return;
    }

    handleFilterChange({
      ...filters,
      dateRangePresetId: nextDateRangePresetId,
      deliveryDateRangePresetId: nextDeliveryDateRangePresetId,
    });
  }, [seasonDatePresets]);

  // Cargar detalles de los pedidos cuando se expandan
  const handleOrderExpand = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (order && !order.items) {
      await fetchOrderDetails(orderId);
    }
  };

  const handleShowPendingInfo = async (order) => {
    try {
      if (order.status === 'partially_served_cancelled') {
        const { data: cancelledOrder, error: cancelledOrderError } = await supabase
          .from('cancelled_orders')
          .select('id')
          .eq('original_order_id', order.id)
          .limit(1)
          .maybeSingle();

        if (cancelledOrderError) throw cancelledOrderError;
        if (!cancelledOrder?.id) {
          throw new Error('No se encontro el historial de cancelacion del pedido');
        }

        const { data: cancelledItems, error: cancelledItemsError } = await supabase
          .from('cancelled_order_items')
          .select(`
            id,
            pending_quantity,
            size,
            color,
            product:products(
              id,
              code,
              description
            )
          `)
          .eq('cancelled_order_id', cancelledOrder.id)
          .gt('pending_quantity', 0);

        if (cancelledItemsError) throw cancelledItemsError;

        if (!cancelledItems || cancelledItems.length === 0) {
          toast({
            title: "Sin pendientes",
            description: `El pedido #${order.number} no tiene lineas pendientes registradas en la cancelacion.`,
          });
          return;
        }

        setSelectedPendingInfoOrder({
          ...order,
          items: cancelledItems,
        });
        setShowPendingInfoDialog(true);
        return;
      }

      let orderWithDetails = order;
      if (!orderWithDetails.items || orderWithDetails.items.length === 0) {
        orderWithDetails = await fetchOrderDetails(order.id);
      }

      const pendingItems = (orderWithDetails?.items || []).filter((item) => (Number(item.pending_quantity) || 0) > 0);
      if (pendingItems.length === 0) {
        toast({
          title: "Sin pendientes",
          description: `El pedido #${order.number} no tiene lineas pendientes de servir.`,
        });
        return;
      }

      setSelectedPendingInfoOrder({
        ...order,
        items: pendingItems,
      });
      setShowPendingInfoDialog(true);
    } catch (error) {
      console.error('Error loading pending items summary:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el resumen de lineas pendientes",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (orderId) => {
    try {
      const { data: orderMeta, error: orderMetaError } = await supabase
        .from('orders')
        .select('status')
        .eq('id', orderId)
        .single();

      if (orderMetaError) throw orderMetaError;

      const { data: servedItems, error: servedItemsError } = await supabase
        .from('order_items')
        .select('id')
        .eq('order_id', orderId)
        .gt('delivered_quantity', 0)
        .limit(1);

      if (servedItemsError) throw servedItemsError;

      const hasServedLines = (servedItems || []).length > 0;
      if (orderMeta?.status === 'partially_served_cancelled' || hasServedLines) {
        toast({
          title: "Borrado no permitido",
          description: "Este pedido tiene líneas servidas. Debe mantenerse por trazabilidad y no se puede eliminar.",
          variant: "destructive",
        });
        return;
      }

      const { data: orderItems, error: fetchError } = await supabase
        .from('order_items')
        .select('product_id, size, color, pending_quantity')
        .eq('order_id', orderId);

      if (fetchError) throw fetchError;

      for (const item of orderItems || []) {
        const { data: existing, error: inventoryError } = await supabase
          .from('inventory')
          .select('pending_orders')
          .eq('product_id', item.product_id)
          .eq('size', item.size)
          .eq('color', item.color)
          .maybeSingle();

        if (inventoryError && inventoryError.code !== 'PGRST116') throw inventoryError;

        const currentPending = existing?.pending_orders || 0;
        const newPending = Math.max(0, currentPending - item.pending_quantity);

        const { error: updateError } = await supabase
          .from('inventory')
          .upsert([{
            product_id: item.product_id,
            size: item.size,
            color: item.color,
            pending_orders: newPending,
            created_at: new Date().toISOString()
          }], { onConflict: ['product_id', 'size', 'color'] });

        if (updateError) throw updateError;
      }

      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderId);

      if (itemsError) throw itemsError;

      const { error: orderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (orderError) throw orderError;

      await fetchOrders();
      toast({
        title: "Pedido eliminado",
        description: "El pedido se ha eliminado exitosamente",
      });
    } catch (error) {
      console.error('Error deleting order:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el pedido",
        variant: "destructive"
      });
    } finally {
      setShowDeleteDialog(false);
      setOrderToDelete(null);
    }
  };

  const handleCancelOrder = (order) => {
    setOrderToCancel(order);
    setShowCancellationDialog(true);
  };

  const handleOrderCancelled = () => {
    fetchOrders();
  };

  const getStatusBadge = (status, hasPendingItems) => {
    const statusMap = {
      'pending': { 
        label: 'Pendiente', 
        className: 'bg-yellow-100 text-yellow-800'
      },
      'served': { 
        label: 'Servido', 
        className: 'bg-green-100 text-green-800' 
      },
      'partially_served': { 
        label: 'Servido parcialmente', 
        className: 'bg-orange-100 text-green-800' 
      },
      'partially_served_cancelled': {
        label: 'Parcialmente servido cancelado',
        className: 'bg-red-100 text-red-800'
      }
    };

    const { label, className } = statusMap[status] || { 
      label: status, 
      className: 'bg-gray-100 text-gray-800' 
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>
        {label}
      </span>
    );
  };

  const renderPendingInfoButton = (order, size = "icon") => {
    if (!['partially_served', 'partially_served_cancelled'].includes(order.status)) {
      return (
        <span
          className={size === "sm" ? "inline-flex h-8 w-8 shrink-0" : "inline-flex h-8 w-8 shrink-0"}
          aria-hidden="true"
        />
      );
    }

    return (
      <Button
        variant="ghost"
        size={size}
        className={`h-8 w-8 shrink-0 p-0 ${isEmailLoading ? "animate-pulse" : ""}`}
        onClick={() => handleShowPendingInfo(order)}
        title="Ver lineas pendientes"
      >
        <Info className="h-4 w-4" />
      </Button>
    );
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
    setCurrentPage(1);
    // Guardar búsqueda en storage
    saveSearchToStorage(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearchTerm("");
    setCurrentPage(1);
    // Limpiar búsqueda del storage
    saveSearchToStorage("");
  };

  const clearFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setSearchTerm("");
    setAppliedSearchTerm("");
    setCurrentPage(1);
    // Limpiar storage
    clearStorage();
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Resetear a la primera página cuando cambien los filtros
    // Guardar filtros en storage
    saveFiltersToStorage(newFilters);
  };

  const toggleStatusFilter = (statusValue) => {
    const currentStatuses = filters.status || [];
    const nextStatuses = currentStatuses.includes(statusValue)
      ? currentStatuses.filter((status) => status !== statusValue)
      : [...currentStatuses, statusValue];

    handleFilterChange({
      ...filters,
      status: nextStatuses,
    });
  };

  const renderEmailButton = (order) => {
    if (!companyInfo) {
      return (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 p-0"
          disabled={true}
          title="Cargando datos de la empresa..."
        >
          <Mail className="h-4 w-4" />
        </Button>
      );
    }

    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 p-0"
        onClick={async () => {
          setEmailError(null);
          setIsEmailLoading(true);
          
          try {
            // Verificar configuración de email
            const { data: emailSettings, error: settingsError } = await supabase
              .from('app_settings')
              .select('email_settings')
              .not('email_settings', 'is', null)
              .limit(1);

            if (settingsError || !emailSettings?.[0]?.email_settings) {
              throw new Error('Configuración de email no encontrada. Configure el envío de emails en Configuración > Email.');
            }

            const config = emailSettings[0].email_settings;
            if (!config.from_email) {
              throw new Error('Configuración de email incompleta. Configure el email remitente.');
            }

            // Obtener email del cliente
            const recipientEmail = order.customer?.email;
            if (!recipientEmail) {
              throw new Error('El cliente no tiene un email configurado.');
            }

            // Cargar detalles completos del pedido si no están disponibles (igual que en renderPDFButton)
            let orderWithDetails = order;
            if (!order.items || order.items.length === 0) {
              console.log('Order has no items, fetching details for email...');
              
              // Mostrar toast de carga
              toast({
                title: "Cargando detalles",
                description: "Obteniendo información del pedido...",
                duration: 2000,
              });
              
              // Probar la consulta primero
              const queryTest = await testOrderQuery(order.id);
              if (!queryTest) {
                throw new Error('No se pudo conectar con la base de datos. Verifique su conexión a internet.');
              }
              
              try {
                const orderDetails = await fetchOrderDetails(order.id);
                console.log('Order details fetched successfully for email:', orderDetails);
                
                if (!orderDetails || !orderDetails.items || orderDetails.items.length === 0) {
                  throw new Error('No se pudieron cargar los detalles del pedido. Verifique su conexión a internet e inténtelo de nuevo.');
                }
                
                orderWithDetails = {
                  ...order,
                  ...orderDetails
                };
                
                console.log('Order with details prepared for email:', orderWithDetails);
                
              } catch (fetchError) {
                console.error('Error in fetchOrderDetails for email:', fetchError);
                
                // Intentar actualización forzada como último recurso
                console.log('🔄 Attempting force update for email as fallback...');
                try {
                  const forceOrderDetails = await forceOrderUpdate(order.id);
                  console.log('Force update successful for email:', forceOrderDetails);
                  
                  orderWithDetails = {
                    ...order,
                    ...forceOrderDetails
                  };
                  
                  console.log('Order with details prepared from force update for email:', orderWithDetails);
                  
                } catch (forceError) {
                  console.error('Force update also failed for email:', forceError);
                  throw fetchError; // Lanzar el error original
                }
              }
            } else {
              console.log('Order already has items for email, using existing data');
            }

            // Preparar el pedido para el PDF con validaciones adicionales (igual que en renderPDFButton)
            const pdfOrderData = {
              ...orderWithDetails,
              number: orderWithDetails.number || orderWithDetails.id || 'sin-numero',
              items: orderWithDetails.items.map(item => ({
                ...item,
                product: {
                  ...item.product,
                  code: item.product?.code || 'N/A',
                  description: item.product?.description || 'Producto sin descripción',
                  images: item.product?.images || [],
                  main_image_index: item.product?.main_image_index || 0
                },
                quantity: Number(item.quantity) || 0,
                price: Number(item.price) || 0,
                size: item.size || 'N/A',
                color: item.color || 'N/A'
              }))
            };

            console.log('PDF order prepared successfully for email:', pdfOrderData);
            console.log('Items count for email PDF:', pdfOrderData.items?.length);
            console.log('First item for email PDF:', pdfOrderData.items?.[0]);
            console.log('Company info for email PDF:', companyInfo);
            
            // Validar que los datos sean válidos antes de generar PDF
            if (!pdfOrderData.items || pdfOrderData.items.length === 0) {
              throw new Error('No hay productos en el pedido para generar PDF');
            }
            
            // Validar que cada item tenga los datos mínimos
            const invalidItems = pdfOrderData.items.filter(item => 
              !item.product || !item.product.code
            );
            
            if (invalidItems.length > 0) {
              console.warn('Items inválidos encontrados:', invalidItems);
            }

            // Generar PDF usando el mismo método que PDFDownloadLink
            const { pdf } = await import('@react-pdf/renderer');
            const { OrderPDF } = await import('./OrderPDF');
            
            // Crear el documento directamente sin Suspense ni elementos DOM
            const doc = <OrderPDF order={pdfOrderData} companyInfo={companyInfo} />;

            // Generar el PDF usando el componente OrderPDF con timeout
            const pdfPromise = pdf(doc).toBlob();
            
            // Timeout de 30 segundos para evitar que se quede colgado
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Timeout generando PDF')), 30000);
            });
            
            const pdfStream = await Promise.race([pdfPromise, timeoutPromise]);
            
            // Convertir Blob a base64 con FileReader para evitar desbordes de pila
            const arrayBuffer = await pdfStream.arrayBuffer();
            const pdfSizeMB = arrayBuffer.byteLength / (1024 * 1024);
            console.log(`PDF generado: ${pdfSizeMB.toFixed(2)}MB`);
            
            if (arrayBuffer.byteLength > 15 * 1024 * 1024) { // 15MB límite aumentado
              throw new Error(`PDF demasiado grande (${pdfSizeMB.toFixed(2)}MB, máximo 15MB). Considere reducir el número de productos.`);
            }
            
            const pdfBase64 = await new Promise((resolve, reject) => {
              try {
                const reader = new FileReader();
                reader.onload = () => {
                  const result = reader.result;
                  const base64 = typeof result === 'string' ? result.split(',')[1] : '';
                  resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(new Blob([arrayBuffer], { type: 'application/pdf' }));
              } catch (e) {
                reject(e);
              }
            });

            // Llamar a la Edge Function directamente a Supabase
            const response = await fetch('https://dundlwzoomxcqbulnylj.supabase.co/functions/v1/send-order-email', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR1bmRsd3pvb214Y3FidWxueWxqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ5MzUxNDAsImV4cCI6MjA2MDUxMTE0MH0.Zhpv_6T5o03HJM65PCfz4QpD41Ehl56ML7qRMDoRfks`,
                'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR1bmRsd3pvb214Y3FidWxueWxqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ5MzUxNDAsImV4cCI6MjA2MDUxMTE0MH0.Zhpv_6T5o03HJM65PCfz4QpD41Ehl56ML7qRMDoRfks'
              },
              body: JSON.stringify({
                orderId: order.id,
                recipientEmail: recipientEmail,
                customMessage: `Pedido #${order.number} enviado automáticamente desde el sistema de gestión.`,
                pdfBase64: pdfBase64
              })
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || `Error HTTP ${response.status}`);
            }

            const result = await response.json();

            if (!result || !result.success) {
              throw new Error(result?.error || 'Error al enviar el email');
            }

            toast({
              title: "Email enviado",
              description: (() => {
                const to = Array.isArray(result.to) ? result.to.join(', ') : recipientEmail;
                const bcc = Array.isArray(result.bcc) && result.bcc.length > 0 ? ` (CCO: ${result.bcc.join(', ')})` : '';
                return `El pedido #${order.number} se ha enviado a: ${to}${bcc}`;
              })(),
              duration: 3000,
            });

          } catch (error) {
            console.error('Error enviando email:', error);
            setEmailError(error);
            
            toast({
              title: "Error al enviar email",
              description: error.message || "No se pudo enviar el email del pedido",
              variant: "destructive",
              duration: 5000,
            });
          } finally {
            setIsEmailLoading(false);
          }
        }}
        disabled={isEmailLoading}
        title={isEmailLoading ? "Enviando email..." : "Enviar pedido por email"}
      >
        {isEmailLoading ? (
          <div className="flex items-center gap-1">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
            <Mail className="h-4 w-4" />
          </div>
        ) : (
          <Mail className="h-4 w-4" />
        )}
      </Button>
    );
  };

  const renderPDFButton = (order) => {
    if (!companyInfo) {
      return (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 p-0"
          disabled={true}
          title="Cargando datos de la empresa..."
        >
          <Printer className="h-4 w-4" />
        </Button>
      );
    }

    return (
      <Button
        variant="ghost"
        size="icon"
        className={`h-8 w-8 shrink-0 p-0 ${isPdfLoading ? "animate-pulse" : ""}`}
        onClick={async () => {
          setPdfError(null);
          setIsPdfLoading(true);
          
          // Timeout para evitar que el proceso se quede colgado
          const timeoutId = setTimeout(() => {
            if (isPdfLoading) {
              handlePdfError(new Error('Tiempo de espera agotado. El proceso de generación del PDF tardó demasiado.'), 'Timeout');
            }
          }, 30000); // 30 segundos de timeout
          
          try {
            console.log('Starting PDF generation for order:', order);
            
            // Validar que el pedido tenga datos básicos
            if (!order || !order.id) {
              throw new Error('Datos del pedido incompletos');
            }

            console.log('Order validation passed, checking items...');

            // Cargar detalles completos del pedido si no están disponibles
            let orderWithDetails = order;
            if (!order.items || order.items.length === 0) {
              console.log('Order has no items, fetching details...');
              
              // Mostrar toast de carga
              toast({
                title: "Cargando detalles",
                description: "Obteniendo información del pedido...",
                duration: 2000,
              });
              
              // Probar la consulta primero
              const queryTest = await testOrderQuery(order.id);
              if (!queryTest) {
                throw new Error('No se pudo conectar con la base de datos. Verifique su conexión a internet.');
              }
              
              try {
                const orderDetails = await fetchOrderDetails(order.id);
                console.log('Order details fetched successfully:', orderDetails);
                
                // Usar directamente los datos cargados en lugar de buscarlos en el estado
                if (!orderDetails || !orderDetails.items || orderDetails.items.length === 0) {
                  throw new Error('No se pudieron cargar los detalles del pedido. Verifique su conexión a internet e inténtelo de nuevo.');
                }
                
                // Usar directamente los datos cargados
                orderWithDetails = {
                  ...order,
                  ...orderDetails
                };
                
                console.log('Order with details prepared from fetched data:', orderWithDetails);
                
              } catch (fetchError) {
                console.error('Error in fetchOrderDetails:', fetchError);
                
                // Intentar actualización forzada como último recurso
                console.log('🔄 Attempting force update as fallback...');
                try {
                  const forceOrderDetails = await forceOrderUpdate(order.id);
                  console.log('Force update successful:', forceOrderDetails);
                  
                  // Usar directamente los datos de la actualización forzada
                  orderWithDetails = {
                    ...order,
                    ...forceOrderDetails
                  };
                  
                  console.log('Order with details prepared from force update:', orderWithDetails);
                  
                } catch (forceError) {
                  console.error('Force update also failed:', forceError);
                  throw fetchError; // Lanzar el error original
                }
              }
            } else {
              console.log('Order already has items, using existing data');
            }

            // Validar que el pedido tenga items válidos
            if (!orderWithDetails.items || orderWithDetails.items.length === 0) {
              throw new Error('El pedido no tiene líneas de productos');
            }

            console.log('Items validation passed, checking item details...');

            // Validar que cada item tenga los datos necesarios
            const invalidItems = orderWithDetails.items.filter(item => 
              !item.product || !item.product.code || !item.product.description
            );
            
            if (invalidItems.length > 0) {
              console.warn('Algunos items del pedido tienen datos incompletos:', invalidItems);
              // Continuar con la generación pero mostrar advertencia
              toast({
                title: "Advertencia",
                description: "Algunos productos tienen datos incompletos. El PDF se generará con la información disponible.",
                variant: "default",
                duration: 3000,
              });
            }

            console.log('Preparing PDF order data...');

            // Preparar el pedido para el PDF con validaciones adicionales
            const pdfOrderData = {
              ...orderWithDetails,
              // Asegurar que el número del pedido esté disponible
              number: orderWithDetails.number || orderWithDetails.id || 'sin-numero',
              items: orderWithDetails.items.map(item => ({
                ...item,
                product: {
                  ...item.product,
                  code: item.product?.code || 'N/A',
                  description: item.product?.description || 'Producto sin descripción',
                  images: item.product?.images || [],
                  main_image_index: item.product?.main_image_index || 0
                },
                quantity: Number(item.quantity) || 0,
                price: Number(item.price) || 0,
                size: item.size || 'N/A',
                color: item.color || 'N/A'
              }))
            };

            console.log('PDF order prepared successfully:', pdfOrderData);
            console.log('PDF filename will be:', `pedido-${pdfOrderData.number}.pdf`);
            
            // Mostrar información detallada del pedido
            logOrderDetails(pdfOrderData, 'for PDF generation');
            
            // Validar que el pedido esté listo para generar PDF
            const validation = validateOrderForPDF(pdfOrderData);
            if (!validation.isValid) {
              console.error('PDF validation failed:', validation.issues);
              throw new Error(`El pedido no está listo para generar PDF: ${validation.issues.join(', ')}`);
            }
            
            console.log('PDF validation passed');
            // Abrir ventana inmediatamente para evitar bloqueos de pop-up
            const preOpenedWindow = window.open('', '_blank');
            try {
              const { pdf } = await import('@react-pdf/renderer');
              const { OrderPDF } = await import('./OrderPDF');
              const doc = <OrderPDF order={pdfOrderData} companyInfo={companyInfo} />;
              const pdfBlob = await pdf(doc).toBlob();
              const objectUrl = URL.createObjectURL(pdfBlob);
              if (preOpenedWindow) {
                preOpenedWindow.location = objectUrl;
                toast({
                  title: 'PDF generado',
                  description: `El PDF del pedido #${pdfOrderData.number} se ha abierto en una nueva pestaña.`,
                  duration: 3000,
                });
              } else {
                handlePdfDownload(objectUrl, pdfOrderData);
              }
            } finally {
              setIsPdfLoading(false);
              clearTimeout(timeoutId);
            }
          } catch (error) {
            clearTimeout(timeoutId); // Limpiar timeout en caso de error
            console.error('Error al cargar los detalles del pedido:', error);
            
            // Mostrar información de debugging
            logDebugInfo(order, error);
            
            setPdfError(error);
            setIsPdfLoading(false);
            
            // Mostrar toast con el error
            toast({
              title: "Error al generar PDF",
              description: error.message || "No se pudo generar el PDF del pedido",
              variant: "destructive",
              duration: 5000,
            });
          }
        }}
        disabled={isPdfLoading}
        title={isPdfLoading ? "Generando PDF..." : "Imprimir pedido"}
      >
        {isPdfLoading ? (
          <div className="flex items-center gap-1">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
            <Printer className="h-4 w-4" />
          </div>
        ) : (
          <Printer className="h-4 w-4" />
        )}
      </Button>
    );
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, "PPP", { locale: es });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleRepresentativeChange = (value) => {
    setIsFiltering(value !== 'all');
    handleFilterChange({ ...filters, representative: value });
  };

  const applySeasonPresetToRange = (presetId, rangeField) => {
    const presetField = rangeField === "dateRange" ? "dateRangePresetId" : "deliveryDateRangePresetId";

    if (presetId === "__none__") {
      handleFilterChange({
        ...filters,
        [rangeField]: { from: null, to: null },
        [presetField]: "__none__"
      });
      return;
    }

    const preset = seasonDatePresets.find((p) => p.id === presetId);
    if (!preset) return;
    const range = toDateRangeFromPreset(preset);

    handleFilterChange({
      ...filters,
      [rangeField]: range,
      [presetField]: presetId
    });
  };

  // Función para limpiar el estado del PDF
  const clearPdfState = () => {
    setPdfOrder(null);
    setIsPdfLoading(false);
    setPdfError(null);
  };

  // Función para mostrar información de debugging
  const logDebugInfo = (order, error) => {
    console.group('🔍 PDF Generation Debug Info');
    console.log('Order ID:', order?.id);
    console.log('Order Number:', order?.number);
    console.log('Order has items:', !!order?.items);
    console.log('Items count:', order?.items?.length || 0);
    console.log('Company info loaded:', !!companyInfo);
    console.log('User data loaded:', !!userData);
    console.log('Error details:', error);
    console.log('Error message:', error?.message);
    console.log('Error code:', error?.code);
    console.log('Error stack:', error?.stack);
    console.groupEnd();
  };

  // Función para manejar errores de PDF de forma consistente
  const handlePdfError = (error, context = '') => {
    console.error(`PDF Error ${context}:`, error);
    setPdfError(error);
    setIsPdfLoading(false);
    
    let errorMessage = "No se pudo generar el PDF del pedido";
    if (error.message) {
      errorMessage = error.message;
    } else if (error.includes('network') || error.includes('connection')) {
      errorMessage = "Error de conexión. Verifique su internet e inténtelo de nuevo.";
    } else if (error.includes('timeout')) {
      errorMessage = "Tiempo de espera agotado. Inténtelo de nuevo.";
    }
    
    toast({
      title: "Error al generar PDF",
      description: errorMessage,
      variant: "destructive",
      duration: 5000,
    });
  };

  // Función para probar la consulta de un pedido específico
  const testOrderQuery = async (orderId) => {
    try {
      console.log('🧪 Testing order query for ID:', orderId);
      
      // Probar consulta básica primero
      const { data: basicData, error: basicError } = await supabase
        .from('orders')
        .select('id, number')
        .eq('id', orderId)
        .single();
      
      if (basicError) {
        console.error('❌ Basic order query failed:', basicError);
        return false;
      }
      
      console.log('✅ Basic order query successful:', basicData);
      
      // Probar consulta de items
      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select('id, quantity, product_id')
        .eq('order_id', orderId);
      
      if (itemsError) {
        console.error('❌ Items query failed:', itemsError);
        return false;
      }
      
      console.log('✅ Items query successful:', itemsData);
      
      // Probar consulta completa
      const { data: fullData, error: fullError } = await supabase
        .from('orders')
        .select(`
          id,
          items:order_items(
            id,
            product:products(
              id,
              code
            )
          )
        `)
        .eq('id', orderId)
        .single();
      
      if (fullError) {
        console.error('❌ Full query failed:', fullError);
        return false;
      }
      
      console.log('✅ Full query successful:', fullData);
      return true;
      
    } catch (error) {
      console.error('❌ Test query error:', error);
      return false;
    }
  };

  // Función para verificar y forzar la actualización del estado
  const forceOrderUpdate = async (orderId) => {
    try {
      console.log('🔄 Force updating order state for ID:', orderId);
      
      // Obtener los datos directamente
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          number,
          date,
          total,
          status,
          notes,
          fecha_entrega,
          forma_pago,
          cod_rep,
          signature,
          confirmacion_90dias,
          shipping_address_id,
          customer:customers(
            name,
            email,
            phone,
            titular,
            street,
            city,
            province,
            postalCode,
            sepa_signature
          ),
          items:order_items(
            id,
            quantity,
            pending_quantity,
            price,
            size,
            color,
            product:products(
              id,
              code,
              description,
              images,
              main_image_index
            )
          ),
          shipping_address:shipping_addresses(
            id,
            name,
            street,
            city
          )
        `)
        .eq('id', orderId)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Pedido no encontrado');
      if (!data.items || data.items.length === 0) {
        throw new Error('El pedido no tiene líneas de productos');
      }

      // Forzar la actualización del estado
      setOrders(prevOrders => {
        const newOrders = prevOrders.map(order => 
          order.id === orderId ? { ...order, items: data.items, shipping_address: data.shipping_address } : order
        );
        console.log('🔄 State updated with forced refresh');
        return newOrders;
      });

      return data;
    } catch (error) {
      console.error('❌ Force update failed:', error);
      throw error;
    }
  };

  // Función para validar que el pedido esté listo para generar PDF
  const validateOrderForPDF = (order) => {
    const issues = [];
    
    if (!order) {
      issues.push('Pedido no definido');
      return { isValid: false, issues };
    }
    
    if (!order.number && !order.id) {
      issues.push('Pedido sin número de referencia');
    }
    
    if (!order.items || order.items.length === 0) {
      issues.push('Pedido sin líneas de productos');
    }
    
    if (!companyInfo) {
      issues.push('Datos de empresa no cargados');
    }
    
    // Verificar que cada item tenga los datos mínimos
    if (order.items) {
      order.items.forEach((item, index) => {
        if (!item.product) {
          issues.push(`Item ${index + 1}: Producto no definido`);
        } else if (!item.product.code) {
          issues.push(`Item ${index + 1}: Código de producto no definido`);
        }
        if (!item.quantity || item.quantity <= 0) {
          issues.push(`Item ${index + 1}: Cantidad inválida`);
        }
      });
    }
    
    return {
      isValid: issues.length === 0,
      issues
    };
  };

  // Función para mostrar información detallada del pedido
  const logOrderDetails = (order, context = '') => {
    console.group(`📋 Order Details ${context}`);
    console.log('Order ID:', order?.id);
    console.log('Order Number:', order?.number);
    console.log('Order Date:', order?.date);
    console.log('Order Total:', order?.total);
    console.log('Items Count:', order?.items?.length || 0);
    console.log('Company Info:', !!companyInfo);
    console.log('Customer:', order?.customer?.name);
    
    if (order?.items) {
      console.log('Items:');
      order.items.forEach((item, index) => {
        console.log(`  ${index + 1}. ${item.product?.code || 'N/A'} - ${item.product?.description || 'N/A'} - Qty: ${item.quantity} - Price: ${item.price}`);
      });
    }
    console.groupEnd();
  };

  // Función para manejar la descarga del PDF de forma segura
  const handlePdfDownload = (url, pdfOrder) => {
    // Usar setTimeout para evitar conflictos con React
    setTimeout(() => {
      try {
        console.log('🔄 Attempting to open PDF:', url);
        const newWindow = window.open(url, '_blank');
        
        if (!newWindow) {
          console.warn('Window blocked, showing toast instead');
          toast({
            title: "PDF generado",
            description: "El PDF se ha generado correctamente. Si no se abrió automáticamente, revise su bloqueador de ventanas emergentes.",
            duration: 4000,
          });
        } else {
          console.log('✅ PDF opened successfully');
          toast({
            title: "PDF generado",
            description: `El PDF del pedido #${pdfOrder.number || 'sin número'} se ha generado y abierto correctamente.`,
            duration: 3000,
          });
        }
      } catch (windowError) {
        console.error('❌ Error opening PDF window:', windowError);
        toast({
          title: "PDF generado",
          description: "El PDF se ha generado correctamente pero no se pudo abrir automáticamente.",
          duration: 4000,
        });
      } finally {
        clearPdfState();
      }
    }, 100); // Pequeño delay para asegurar que React haya terminado
  };

  if (viewMode === "pending") {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <Button variant="outline" onClick={() => setViewMode("list")}>
            Volver a la lista
          </Button>
        </div>
        <PendingOrders onOrderServed={fetchOrders} />
      </div>
    );
  }

  if (showCancelledOrders) {
    return (
      <CancelledOrdersList 
        onBack={() => setShowCancelledOrders(false)} 
      />
    );
  }

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 flex-1">
            <div className="flex gap-2 max-w-sm">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="search"
                  placeholder="Buscar por número de pedido o cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleSearch}
                className="flex items-center gap-2 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              >
                <Search className="h-4 w-4" />
                Buscar
              </Button>
              {appliedSearchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSearch}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              <Filter className="h-4 w-4" />
              Filtros
              {(filters.status?.length > 0 || filters.dateRange.from || filters.dateRange.to || filters.deliveryDateRange?.from || filters.deliveryDateRange?.to || filters.paymentMethod !== "all" || filters.representative !== "all" || !!filters.productId) && (
                <Badge variant="secondary" className="ml-2 bg-green-600 text-white">
                  {[
                    filters.status?.length > 0,
                    filters.dateRange.from || filters.dateRange.to,
                    filters.deliveryDateRange?.from || filters.deliveryDateRange?.to,
                    filters.paymentMethod !== "all",
                    filters.representative !== "all",
                    !!filters.productId
                  ].filter(Boolean).length}
                </Badge>
              )}
            </Button>
            {canViewAllOrders && (
              <Button 
                variant="outline" 
                onClick={() => setViewMode("pending")}
                className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
              >
                Gestionar pedidos
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowCancelledOrders(true)}
              className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Historial Cancelados
            </Button>
          </div>
          <Button 
            onClick={() => window.location.hash = "#/pedido/new"}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
          >
            <Plus className="mr-2 h-4 w-4" /> Nuevo Pedido
          </Button>
        </div>

        {showFilters && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <span className="text-green-600">🔍</span>
                <h3 className="font-medium text-gray-800">Filtros</h3>
                {filtersRestored && (
                  <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">
                    Restaurados
                  </Badge>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-4 w-4 mr-2" />
                Limpiar filtros
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Estado</label>
                <div className="border rounded-md bg-white p-2 space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(filters.status || []).length === 0}
                      onChange={() => handleFilterChange({ ...filters, status: [] })}
                    />
                    Todos
                  </label>
                  {ORDER_STATUS_OPTIONS.map((statusOption) => (
                    <label key={statusOption.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.status || []).includes(statusOption.value)}
                        onChange={() => toggleStatusFilter(statusOption.value)}
                      />
                      {statusOption.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Rango de fechas</label>
                <Select
                  value={filters.dateRangePresetId || "__none__"}
                  onValueChange={(value) => applySeasonPresetToRange(value, "dateRange")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Temporada (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Todos / sin temporada</SelectItem>
                    {seasonDatePresets.map((preset) => (
                      <SelectItem key={`order-date-${preset.id}`} value={preset.id}>
                        {preset.name} ({preset.from} - {preset.to})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {filters.dateRange.from ? (
                          formatDate(filters.dateRange.from)
                        ) : (
                          "Desde"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.dateRange.from}
                        onSelect={(date) =>
                          handleFilterChange({
                            ...filters,
                            dateRange: { ...filters.dateRange, from: date },
                            dateRangePresetId: "__none__"
                          })
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {filters.dateRange.to ? (
                          formatDate(filters.dateRange.to)
                        ) : (
                          "Hasta"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.dateRange.to}
                        onSelect={(date) =>
                          handleFilterChange({
                            ...filters,
                            dateRange: { ...filters.dateRange, to: date },
                            dateRangePresetId: "__none__"
                          })
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Fecha de entrega</label>
                <Select
                  value={filters.deliveryDateRangePresetId || "__none__"}
                  onValueChange={(value) => applySeasonPresetToRange(value, "deliveryDateRange")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Temporada (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Todos / sin temporada</SelectItem>
                    {seasonDatePresets.map((preset) => (
                      <SelectItem key={`delivery-date-${preset.id}`} value={preset.id}>
                        {preset.name} ({preset.from} - {preset.to})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {filters.deliveryDateRange?.from ? (
                          formatDate(filters.deliveryDateRange.from)
                        ) : (
                          "Desde"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.deliveryDateRange?.from}
                        onSelect={(date) =>
                          handleFilterChange({
                            ...filters,
                            deliveryDateRange: { ...filters.deliveryDateRange, from: date },
                            deliveryDateRangePresetId: "__none__"
                          })
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {filters.deliveryDateRange?.to ? (
                          formatDate(filters.deliveryDateRange.to)
                        ) : (
                          "Hasta"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.deliveryDateRange?.to}
                        onSelect={(date) =>
                          handleFilterChange({
                            ...filters,
                            deliveryDateRange: { ...filters.deliveryDateRange, to: date },
                            deliveryDateRangePresetId: "__none__"
                          })
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Forma de pago</label>
                <Select
                  value={filters.paymentMethod}
                  onValueChange={(value) => handleFilterChange({ ...filters, paymentMethod: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar forma de pago" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="0">Contado</SelectItem>
                    <SelectItem value="30">30 días</SelectItem>
                    <SelectItem value="60">60 días</SelectItem>
                    <SelectItem value="90">90 días</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(userRole === 'admin' || canViewAllOrders) && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Representante</label>
                  <Select
                    value={filters.representative}
                    onValueChange={handleRepresentativeChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar representante" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {representatives
                        .filter((rep) => rep && rep.cod_rep != null && String(rep.cod_rep).trim() !== "")
                        .map((rep) => (
                          <SelectItem key={String(rep.cod_rep)} value={String(rep.cod_rep)}>
                            {rep.name} ({rep.cod_rep})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Filtro por producto */}
              <div className="space-y-2 md:col-span-4">
                <label className="text-sm font-medium">Producto</label>
                <ProductSelector
                  value={filters.productId}
                  onSelect={(productId) => handleFilterChange({ ...filters, productId })}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Generación de PDF directa: se elimina el uso de PDFDownloadLink para evitar bloqueos de ventana */}

      {pdfError && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg z-50 max-w-sm">
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium">Error al generar PDF</h4>
              <p className="text-sm mt-1">{pdfError.message}</p>
              <div className="flex gap-2 mt-2">
                <button 
                  onClick={() => setPdfError(null)}
                  className="text-red-600 hover:text-red-800 text-sm underline"
                >
                  Cerrar
                </button>
                <button 
                  onClick={() => {
                    setPdfError(null);
                    // Reintentar la generación del PDF
                    const currentOrder = orders.find(o => o.id === pdfOrder?.id);
                    if (currentOrder) {
                      renderPDFButton(currentOrder).props.onClick();
                    }
                  }}
                  className="text-red-600 hover:text-red-800 text-sm underline"
                >
                  Reintentar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vista de tabla para pantallas grandes */}
      <div className="hidden lg:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50">
              <TableHead className="font-semibold text-blue-800">Nº Pedido</TableHead>
              <TableHead className="font-semibold text-blue-800">Fecha</TableHead>
              <TableHead className="font-semibold text-blue-800">Cliente</TableHead>
              <TableHead className="font-semibold text-blue-800">Fecha Entrega</TableHead>
              <TableHead className="font-semibold text-blue-800 w-[8rem]">Formas de Pago</TableHead>
              <TableHead className="font-semibold text-blue-800">Dirección Envío</TableHead>
              <TableHead className="font-semibold text-blue-800">Total</TableHead>
              <TableHead className="font-semibold text-blue-800">Estado</TableHead>
              <TableHead className="font-semibold text-blue-800 w-[10rem]">Observaciones</TableHead>
              <TableHead className="text-right font-semibold text-blue-800 w-[7rem]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell className="font-medium">#{order.number}</TableCell>
                <TableCell>{new Date(order.date).toLocaleDateString()}</TableCell>
                <TableCell>{order.customer?.name}</TableCell>
                <TableCell>{order.fecha_entrega ? new Date(order.fecha_entrega).toLocaleDateString() : '-'}</TableCell>
                <TableCell className="w-[8rem]">
                  <div className="flex flex-wrap gap-1">
                    {order.forma_pago?.map((dias, index) => (
                    <span key={index} className={`inline-block rounded-full px-1.5 py-0.5 text-[11px] font-medium ${
                      dias === 30 ? 'bg-blue-100 text-blue-700' :
                      dias === 60 ? 'bg-green-100 text-green-700' :
                      dias === 90 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {dias} días
                      {dias === 90 && !order.confirmacion_90dias && (
                        <span className="ml-1 text-yellow-600 font-bold">(Pendiente)</span>
                      )}
                    </span>
                  ))}
                  </div>
                </TableCell>
                <TableCell>
                  {order.shipping_address_id ? (
                    shippingAddresses[order.id] ? (
                      <div className="text-sm">
                        <p className="font-medium">{shippingAddresses[order.id].name}</p>
                        <p className="text-gray-500">
                          {shippingAddresses[order.id].street}, {shippingAddresses[order.id].city}
                        </p>
                        <p className="text-blue-600 text-xs font-medium">Dirección de envío</p>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <p className="text-blue-600 font-medium">Dirección de envío</p>
                        <p className="text-gray-500">Cargando detalles...</p>
                      </div>
                    )
                  ) : (
                    <div className="text-sm">
                      <p className="text-gray-600 font-medium">Dirección fiscal</p>
                      <p className="text-gray-500">Cliente: {order.customer?.name}</p>
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-bold text-green-600">{formatCurrency(order.total)}</TableCell>
                <TableCell>{getStatusBadge(order.status, order.hasPendingItems)}</TableCell>
                <TableCell className="w-[10rem] max-w-[10rem] truncate">
                  <div className="space-y-1">
                    {order.notes && <p className="truncate">{order.notes}</p>}
                    <div className="flex flex-col gap-1">
                      {!order.signature && (
                        <p className="text-yellow-600 text-sm flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Pedido sin firma
                        </p>
                      )}
                      {!order.customer?.sepa_signature && (
                        <p className="text-yellow-600 text-sm flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Cliente sin firma SEPA
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right w-[7rem]">
                  <div className="flex justify-end gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 p-0"
                      onClick={() => {
                        handleOrderExpand(order.id);
                        window.location.hash = `#/pedido/${order.id}`;
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {renderPendingInfoButton(order)}
                    {renderPDFButton(order)}
                    {renderEmailButton(order)}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 p-0"
                      onClick={() => handleCancelOrder(order)}
                      title="Cancelar pedido"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Vista de tarjetas para pantallas medianas */}
      <div className="hidden md:block lg:hidden space-y-4">
        {orders.map((order) => (
          <div key={order.id} className="bg-white border rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-semibold text-lg">#{order.number}</h3>
                  {getStatusBadge(order.status, order.hasPendingItems)}
                  <span className="text-lg font-bold text-green-600">{formatCurrency(order.total)}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Cliente: <span className="font-medium">{order.customer?.name}</span></p>
                    <p className="text-gray-600">Fecha: <span className="font-medium">{new Date(order.date).toLocaleDateString()}</span></p>
                    {order.fecha_entrega && (
                      <p className="text-gray-600">Entrega: <span className="font-medium">{new Date(order.fecha_entrega).toLocaleDateString()}</span></p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-600">Formas de pago:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {order.forma_pago?.map((dias, index) => (
                        <span key={index} className="inline-block bg-gray-100 rounded-full px-2 py-1 text-xs">
                          {dias} días
                          {dias === 90 && !order.confirmacion_90dias && (
                            <span className="ml-1 text-yellow-600">(Pendiente)</span>
                          )}
                        </span>
                      ))}
                      {order.payment_transfer && (
                        <span className="inline-block bg-blue-100 text-blue-700 rounded-full px-2 py-1 text-xs">
                          💳 Transferencia
                        </span>
                      )}
                                              {order.discount_percentage > 0 && (
                          <span className="inline-block bg-green-100 text-green-700 rounded-full px-2 py-1 text-xs">
                            💰 {order.discount_percentage}% aplicado
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-0.5 ml-3 min-w-[7rem] justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 p-0"
                  onClick={() => {
                    handleOrderExpand(order.id);
                    window.location.hash = `#/pedido/${order.id}`;
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                {renderPendingInfoButton(order)}
                {renderPDFButton(order)}
                {renderEmailButton(order)}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 p-0"
                  onClick={() => handleCancelOrder(order)}
                  title="Cancelar pedido"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="border-t pt-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 font-medium mb-1">Dirección:</p>
                  {order.shipping_address_id ? (
                    shippingAddresses[order.id] ? (
                      <div>
                        <p className="font-medium">{shippingAddresses[order.id].name}</p>
                        <p className="text-gray-500">
                          {shippingAddresses[order.id].street}, {shippingAddresses[order.id].city}
                        </p>
                        <p className="text-blue-600 text-xs font-medium">Dirección de envío</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-blue-600 font-medium">Dirección de envío</p>
                        <p className="text-gray-500">Cargando detalles...</p>
                      </div>
                    )
                  ) : (
                    <div>
                      <p className="text-gray-600 font-medium">Dirección fiscal</p>
                      <p className="text-gray-500">Cliente: {order.customer?.name}</p>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-gray-600 font-medium mb-1">Observaciones:</p>
                  <div className="space-y-1">
                    {order.notes && <p className="text-sm">{order.notes}</p>}
                    <div className="flex flex-col gap-1">
                      {!order.signature && (
                        <p className="text-yellow-600 text-xs flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Pedido sin firma
                        </p>
                      )}
                      {!order.customer?.sepa_signature && (
                        <p className="text-yellow-600 text-xs flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Cliente sin firma SEPA
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Vista de tarjetas compactas para pantallas pequeñas */}
      <div className="md:hidden space-y-3">
        {orders.map((order) => (
          <div key={order.id} className="bg-white border rounded-lg p-3 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-base">#{order.number}</h3>
                  {getStatusBadge(order.status, order.hasPendingItems)}
                </div>
                <p className="text-sm text-gray-600 truncate">{order.customer?.name}</p>
                <p className="text-sm text-gray-500">{new Date(order.date).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-0.5 ml-2 min-w-[7rem] justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 shrink-0 p-0"
                  onClick={() => {
                    handleOrderExpand(order.id);
                    window.location.hash = `#/pedido/${order.id}`;
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                {renderPendingInfoButton(order, "sm")}
                {renderPDFButton(order)}
                {renderEmailButton(order)}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 shrink-0 p-0"
                  onClick={() => handleCancelOrder(order)}
                  title="Cancelar pedido"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex justify-between items-center mb-2">
              <span className="text-lg font-bold text-green-600">{formatCurrency(order.total)}</span>
              {order.fecha_entrega && (
                <span className="text-xs text-gray-500">Entrega: {new Date(order.fecha_entrega).toLocaleDateString()}</span>
              )}
            </div>
            
            <div className="flex flex-wrap gap-1 mb-2">
              {order.forma_pago?.map((dias, index) => (
                <span key={index} className="inline-block bg-gray-100 rounded-full px-2 py-1 text-xs">
                  {dias} días
                  {dias === 90 && !order.confirmacion_90dias && (
                    <span className="ml-1 text-yellow-600">(Pendiente)</span>
                  )}
                </span>
              ))}
            </div>
            
            <div className="border-t pt-2">
              <div className="text-xs text-gray-600 space-y-1">
                {order.notes && <p className="truncate">{order.notes}</p>}
                <div className="flex flex-col gap-1">
                  {!order.signature && (
                    <p className="text-yellow-600 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Sin firma
                    </p>
                  )}
                  {!order.customer?.sepa_signature && (
                    <p className="text-yellow-600 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Sin SEPA
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between px-2">
        <div className="flex-1 text-sm text-muted-foreground">
          Mostrando {orders.length} de {totalOrders} pedidos
          {appliedSearchTerm && (
            <span className="ml-2 text-blue-600">
              (buscando: "{appliedSearchTerm}")
            </span>
          )}
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Página {currentPage} de {totalPages}</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              Primera
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            
            {/* Números de página */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Última
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el pedido y se actualizará el inventario.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => orderToDelete && handleDelete(orderToDelete.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CancellationDialog
        isOpen={showCancellationDialog}
        onClose={() => {
          setShowCancellationDialog(false);
          setOrderToCancel(null);
        }}
        order={orderToCancel}
        onOrderCancelled={handleOrderCancelled}
      />

      <Dialog
        open={showPendingInfoDialog}
        onOpenChange={(open) => {
          setShowPendingInfoDialog(open);
          if (!open) setSelectedPendingInfoOrder(null);
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Pendientes de servir del pedido #{selectedPendingInfoOrder?.number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              {selectedPendingInfoOrder?.customer?.name}
            </div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Producto</TableHead>
                    <TableHead>Talla</TableHead>
                    <TableHead>Color</TableHead>
                    <TableHead className="text-right">Pendiente</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(selectedPendingInfoOrder?.items || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.product?.code}</div>
                          <div className="text-sm text-gray-500">{item.product?.description}</div>
                        </div>
                      </TableCell>
                      <TableCell>{item.size}</TableCell>
                      <TableCell>{item.color}</TableCell>
                      <TableCell className="text-right font-medium">{item.pending_quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
